import { GridColDef } from "@mui/x-data-grid";
import { useRepassesSetoriaisDispatch, useRepassesSetoriaisState } from "./repasses-setoriais-context";
import { ACTION_TYPES, initialDataRequest } from "./repasses-setoriais-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IRepasseSetorialResponse } from "../../../../model/admin-portal/response/repasse-setorial-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';

const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const useRepassesSetoriaisController = () => {
    const state = useRepassesSetoriaisState();
    const dispatch = useRepassesSetoriaisDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listRepassesSetoriais(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar repasses setoriais' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const [areas, origens] = await Promise.all([
                AdminPortalAppService.listAreasTransparenciaSetorial(),
                AdminPortalAppService.listOrigensRepasseSetorial()
            ]);
            dispatch({ type: ACTION_TYPES.setAreasList, payload: areas });
            dispatch({ type: ACTION_TYPES.setOrigensList, payload: origens });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar listas de Área/Origem' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getRepasseSetorial(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idAreaTransparenciaSetorial: response.idAreaTransparenciaSetorial,
                    idOrigemRepasseSetorial: response.idOrigemRepasseSetorial,
                    ano: response.ano,
                    textoDescricao: response.textoDescricao,
                    valorPrevisto: response.valorPrevisto,
                    valorRealizado: response.valorRealizado,
                    dtaPublicacao: response.dtaPublicacao
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar repasse setorial' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idAreaTransparenciaSetorial) return false;
        if (!r.idOrigemRepasseSetorial) return false;
        if (!r.ano || r.ano <= 0) return false;
        if (!r.textoDescricao || r.textoDescricao.trim() === '') return false;
        if (!r.valorPrevisto || r.valorPrevisto <= 0) return false;
        if (r.valorRealizado === null || r.valorRealizado === undefined || r.valorRealizado < 0) return false;
        if (!r.dtaPublicacao) return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: IRepasseSetorialResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateRepasseSetorial(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Repasse setorial atualizado com sucesso' : 'Repasse setorial criado com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar repasse setorial' });
        } finally {
            showBackDrop(false);
        }
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'areaName', headerName: 'Área', flex: 1, minWidth: 150 },
            { field: 'origemName', headerName: 'Origem', width: 120 },
            { field: 'ano', headerName: 'Ano', width: 90 },
            {
                field: 'valorPrevisto', headerName: 'Valor Previsto', width: 150,
                valueGetter: (_value, row) => formatCurrency(row.valorPrevisto)
            },
            {
                field: 'valorRealizado', headerName: 'Valor Realizado', width: 150,
                valueGetter: (_value, row) => formatCurrency(row.valorRealizado)
            },
            { field: 'textoDescricao', headerName: 'Descrição', flex: 1.5, minWidth: 220 },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid
    };
};
